import React, { useState, useEffect, useRef, useCallback } from 'react';
import Map, { Marker, Popup, NavigationControl, ScaleControl, Source, Layer } from 'react-map-gl';
import { propertyAPI } from '../services/api';
import OpportunityPanel from '../components/OpportunityPanel';
import DataOverlays from '../components/DataOverlays';
import ExportModal from '../components/ExportModal';
import PropertyList from '../components/PropertyList';
import RangeSlider from '../components/RangeSlider';
import Tooltip from '../components/Tooltip';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import ComprehensiveTractLayer from '../components/ComprehensiveTractLayer';
import TractInfoPanel from '../components/TractInfoPanel';
import CrimeLayer from '../components/CrimeLayer';
import Navbar from '../components/Navbar';
import '../components/map.css';

function DashboardPage() {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewMode, setViewMode] = useState('map');
  const [showExportModal, setShowExportModal] = useState(false);

  // Add overlays state
  const [overlays, setOverlays] = useState({
    censusTracts: false,
    demographics: false,
    crime: false,
    eviction: false,
    income: false
  });

  const [viewport, setViewport] = useState({
    latitude: 39.9612,
    longitude: -82.9988,
    zoom: 11
  });

  const [loading, setLoading] = useState(false);
  const [tractsLoading, setTractsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    units: 0,
    avgYear: 0,
    minYear: 1900,
    maxYear: 2024,
    avgRentSF: 0,
    avgVacancy: 0
  });

  const [propertyTypes, setPropertyTypes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [radiusCenter, setRadiusCenter] = useState(null);
  const [opportunityScores, setOpportunityScores] = useState([]);
  const [showFilterTooltip, setShowFilterTooltip] = useState(true);
  const [selectedTract, setSelectedTract] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [gridLoading, setGridLoading] = useState(false); // Grid loading state
  const [tractDataLayer, setTractDataLayer] = useState('income'); // 'income', 'lmi', 'population', 'minority'
  const [overlayData, setOverlayData] = useState({
    demographics: null,
    crime: null,
    eviction: null,
    income: null
  });
  const [mapSearchTerm, setMapSearchTerm] = useState('');
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [mapSearching, setMapSearching] = useState(false);
  const mapSearchTimeoutRef = useRef(null);

  const handleMapSearchChange = (e) => {
    const value = e.target.value;
    setMapSearchTerm(value);

    if (mapSearchTimeoutRef.current) {
      clearTimeout(mapSearchTimeoutRef.current);
    }

    // wait ~600ms after last keystroke
    mapSearchTimeoutRef.current = setTimeout(() => {
      searchPropertiesOnMap(value);
    }, 600);
  };

  const handleMapSearchSelect = (property) => {
    setSelectedProperty(property);
    setSelectedTract(null); // Clear tract selection to prioritize property context
    setMapSearchResults([]);
    setMapSearchTerm(
      ''
    );
    // Avoid pending search runs after selection
    if (mapSearchTimeoutRef.current) {
      clearTimeout(mapSearchTimeoutRef.current);
      mapSearchTimeoutRef.current = null;
    }


    if (property.latitude && property.longitude && mapRef.current) {
      const map = mapRef.current.getMap();
      map.flyTo({
        center: [property.longitude, property.latitude],
        zoom: 15,
        speed: 1.2,
        curve: 1.4,
        essential: true
      });
    }
  };


  const searchPropertiesOnMap = useCallback(async (term) => {
    if (!term.trim()) {
      setMapSearchResults([]);
      return;
    }

    try {
      setMapSearching(true);
      const res = await propertyAPI.getAllProperties({ search: term.trim() });
      setMapSearchResults(res.data || []);
    } catch (err) {
      console.error('Error searching properties:', err);
      setMapSearchResults([]);
    } finally {
      setMapSearching(false);
    }
  }, []);


  // MODIFIED: Set default filters for optimized load time - Class B, Year Built 1990-2010
  const [filters, setFilters] = useState({
    buildingClasses: ['A', 'B', 'C'], // Default to Class B only
    propertyTypes: [],
    yearRange: { min: 1990, max: 2010 }, // Default to 1990-2010
    unitRange: { min: 50, max: 500 }, // New unit range slider
    radius: null
  });

  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const handlePropertySelect = useCallback((property) => {
    setSelectedProperty(property);
    setSelectedTract(null); // Clear tract selection to prioritize property context

    // If we have coordinates, fly to them
    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);

  }, []);

  // Create radius circle based on current radius filter and center point
  const radiusCircle = React.useMemo(() => {
    if (!radiusCenter || !filters.radius) return null;

    const radiusInKm = filters.radius * 1.60934;
    const points = 64;
    const ret = [];
    const distanceX = radiusInKm / (111.320 * Math.cos(radiusCenter.lat * Math.PI / 180));
    const distanceY = radiusInKm / 110.574;

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI);
      const x = distanceX * Math.cos(theta);
      const y = distanceY * Math.sin(theta);
      ret.push([radiusCenter.lng + x, radiusCenter.lat + y]);
    }
    ret.push(ret[0]);

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ret]
      }
    };
  }, [radiusCenter, filters.radius]);

  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Removed client-side applyFilters logic as we now filter on the backend

  const loadProperties = useCallback(async (bounds = null) => {
    // Determine filters
    const filterParams = {
      buildingClasses: filters.buildingClasses,
      propertyTypes: filters.propertyTypes,
      yearMin: filters.yearRange?.min,
      yearMax: filters.yearRange?.max,
      unitsMin: filters.unitRange?.min,
      unitsMax: filters.unitRange?.max,
      radius: filters.radius,
      lat: radiusCenter?.lat,
      lng: radiusCenter?.lng
    };

    setLoading(true);
    console.log('🔄 Loading properties from API...', { bounds: !!bounds, filters: filterParams });
    try {
      let response;
      if (bounds) {
        response = await propertyAPI.getPropertiesInBounds({
          ...bounds,
          ...filterParams
        });
      } else {
        // If loading all (fallback), apply filters too
        response = await propertyAPI.getAllProperties({ ...filterParams });
      }

      // For Map View, the "properties" state is now the FILTERED properties from backend
      setProperties(response.data);
      // We also update filteredProperties directly because we are filtering on backend now
      setFilteredProperties(response.data);

      console.log('✅ Properties loaded:', response.data.length);
    } catch (error) {
      console.error('❌ Error loading properties:', error);
    }
    setLoading(false);
  }, [filters, radiusCenter]);

  // Load Grid Clusters
  const loadGrid = useCallback(async (bounds) => {
    try {
      setGridLoading(true);
      const response = await propertyAPI.getGridClusters({
        swLat: bounds.getSouthWest().lat,
        swLng: bounds.getSouthWest().lng,
        neLat: bounds.getNorthEast().lat,
        neLng: bounds.getNorthEast().lng,
        resolution: 25000
      });
      setGridData(response.data);
    } catch (error) {
      console.error('Error loading grid:', error);
    } finally {
      setGridLoading(false);
    }
  }, []);

  // Pagination state for List View
  const [listProperties, setListProperties] = useState([]);
  const [listSortBy, setListSortBy] = useState('id');
  const [listSortOrder, setListSortOrder] = useState('ASC');
  const [listSearchTerm, setListSearchTerm] = useState('');

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 1
  });

  const loadListProperties = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const filterParams = {
        buildingClasses: filters.buildingClasses,
        propertyTypes: filters.propertyTypes,
        yearMin: filters.yearRange?.min,
        yearMax: filters.yearRange?.max,
        unitsMin: filters.unitRange?.min,
        unitsMax: filters.unitRange?.max,
        radius: filters.radius,
        lat: radiusCenter?.lat,
        lng: radiusCenter?.lng
      };

      // Use the paginated endpoint as requested
      const response = await propertyAPI.getAllProperties({
        page,
        limit: 50,
        search: listSearchTerm || mapSearchTerm, // Fallback to map search if list specific is empty, or just separate them? Let's use listSearchTerm primarily for list view.
        sortBy: listSortBy,
        order: listSortOrder,
        ...filterParams
      });

      if (response.data && response.data.properties) {
        setListProperties(response.data.properties);
        setPagination({
          page: response.data.page,
          pageSize: response.data.pageSize,
          total: response.data.total,
          totalPages: response.data.totalPages
        });
      }
    } catch (error) {
      console.error('Error loading list properties:', error);
    } finally {
      setLoading(false);
    }
  }, [mapSearchTerm, listSearchTerm, listSortBy, listSortOrder, filters, radiusCenter]);

  // Handle list sort change
  const handleListSortChange = useCallback((field, order) => {
    console.log(`Sorting list by ${field} ${order}`);
    setListSortBy(field);
    setListSortOrder(order);
    // Pagination reset to 1 when sort changes? usually good practice, but user might want to stay on page.
    // Let's keep page 1 for simplicity to avoid empty pages if sort changes total order dramatically?
    // Actually sort order shouldn't change total count, but easier to see "top" results.
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Handle list search
  const handleListSearch = useCallback((term) => {
    console.log(`Searching list for: ${term}`);
    setListSearchTerm(term);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Initial Data Load
  useEffect(() => {
    loadStats();
    loadPropertyTypes();
    loadOpportunityScores();
  }, []);

  // Debounced bounds-based fetching (Map) and Pagination fetching (List)
  useEffect(() => {
    const MIN_ZOOM = 10;

    // Map View Logic
    if (viewMode === 'map' || viewMode === 'split') {
      const timer = setTimeout(() => {
        if (mapRef.current) {
          try {
            const map = mapRef.current.getMap();
            const bounds = map.getBounds();

            // Zoom > 10: Show Properties, Hide Grid
            if (viewport.zoom > 10) {
              setGridData(null);
              loadProperties({
                swLat: bounds.getSouthWest().lat,
                swLng: bounds.getSouthWest().lng,
                neLat: bounds.getNorthEast().lat,
                neLng: bounds.getNorthEast().lng
              });
            }
            // Zoom <= 10: Show Grid, Hide Properties
            else {
              console.log('Zoom <= 10, switching to grid view');
              setProperties(prev => prev.length > 0 ? [] : prev);
              loadGrid(bounds);
            }
          } catch (err) {
            // Map might not be ready yet
            console.log('Map not ready for bounds fetching');
          }
        }
      }, 500);
      return () => clearTimeout(timer);
    }

    // For List view or Split view part 2
    if (viewMode === 'list' || viewMode === 'split') {
      // Load paginated properties
      loadListProperties(pagination.page);
    }
  }, [viewport, viewMode, pagination.page, listSortBy, listSortOrder, listSearchTerm, filters, radiusCenter]);

  const loadStats = async () => {
    console.log('🔄 Loading statistics...');
    try {
      const response = await propertyAPI.getStats();
      const data = response.data;
      setStats({
        total: parseInt(data.totalProperties) || 0,
        units: parseInt(data.totalUnits) || 0,
        avgYear: Math.round(parseFloat(data.avgYearBuilt)) || 0,
        minYear: parseInt(data.minYear) || 1900,
        maxYear: parseInt(data.maxYear) || 2024,
        avgRentSF: parseFloat(data.avgRentSF) || 0,
        avgVacancy: parseFloat(data.avgVacancy) || 0
      });
      console.log('✅ Stats loaded:', data);
    } catch (error) {
      console.error('❌ Error loading stats:', error);
    }
  };

  const loadPropertyTypes = async () => {
    console.log('🔄 Loading property types...');
    try {
      const response = await propertyAPI.getPropertyTypes();
      setPropertyTypes(response.data);
      console.log('✅ Property types loaded:', response.data);
    } catch (error) {
      console.error('❌ Error loading property types:', error);
    }
  };

  const loadOpportunityScores = async () => {
    console.log('🔄 Loading opportunity scores...');
    try {
      const response = await propertyAPI.getOpportunityScores();
      setOpportunityScores(response.data);
      console.log('✅ Opportunity scores loaded:', response.data.length);
    } catch (error) {
      console.error('❌ Error loading opportunity scores:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    console.log(`📤 Starting upload of ${files.length} file(s)...`);
    setUploadProgress(0);
    const totalFiles = files.length;
    let uploaded = 0;

    for (let file of files) {
      try {
        console.log(`   Uploading: ${file.name}...`);
        await propertyAPI.uploadExcel(file);
        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
        console.log(`   ✅ ${file.name} uploaded successfully`);
      } catch (error) {
        console.error(`   ❌ Error uploading ${file.name}:`, error);
      }
    }

    console.log(`✅ Upload complete! ${uploaded}/${totalFiles} files uploaded successfully`);

    setTimeout(() => {
      setUploadProgress(null);
      console.log('🔄 Reloading data after upload...');
      loadProperties();
      loadStats();
      loadPropertyTypes();
      loadOpportunityScores();
    }, 1000);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMapClick = (e) => {
    if (filters.radius) {
      const point = e.lngLat;
      setRadiusCenter({ lat: point.lat, lng: point.lng });
      console.log('📍 Radius center set:', { lat: point.lat, lng: point.lng });
      return;
    }

    // Check for tract clicks
    if (mapRef.current && (overlays.censusTracts || overlays.income || overlays.demographics)) {
      const map = mapRef.current.getMap();

      // Try to query tract layers - check which ones exist first
      let features = [];
      const possibleLayers = ['tract-fill', 'tract-points'];

      for (const layerId of possibleLayers) {
        try {
          const layerFeatures = map.queryRenderedFeatures(e.point, {
            layers: [layerId]
          });
          if (layerFeatures && layerFeatures.length > 0) {
            features = layerFeatures;
            break; // Found features, stop checking other layers
          }
        } catch (err) {
          // Layer doesn't exist, continue to next layer
          continue;
        }
      }

      if (features && features.length > 0) {
        const tractProperties = features[0].properties;
        setSelectedTract(tractProperties);
        console.log('📍 Tract selected:', tractProperties);
      }
    }
  };

  const handleYearRangeChange = useCallback((field, value) => {
    const newValue = parseInt(value) || (field === 'min' ? 1900 : 2024);
    console.log(`📅 Year range ${field} changed to: ${newValue}`);

    setFilters(prev => ({
      ...prev,
      yearRange: { ...prev.yearRange, [field]: newValue }
    }));
  }, []);

  const getMarkerColor = (buildingClass, opportunityScore = 0) => {
    if (opportunityScore > 80) return '#22c55e';
    if (opportunityScore > 60) return '#3b82f6';
    if (opportunityScore > 40) return '#f59e0b';

    switch (buildingClass) {
      case 'A': return '#3b82f6';
      case 'B': return '#f59e0b';
      case 'C': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatNumber = (value) => {
    if (!value) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const filteredStats = React.useMemo(() => {
    const totalUnits = filteredProperties.reduce((sum, p) => sum + (p.numberOfUnits || 0), 0);
    const avgVacancy = filteredProperties.length > 0
      ? filteredProperties.reduce((sum, p) => sum + (p.vacancyPercent || 0), 0) / filteredProperties.length
      : 0;

    const propertiesWithRent = filteredProperties.filter(p => p.rentPerSF && !isNaN(p.rentPerSF));
    const avgRentSF = propertiesWithRent.length > 0
      ? propertiesWithRent.reduce((sum, p) => sum + parseFloat(p.rentPerSF), 0) / propertiesWithRent.length
      : 0;

    return {
      count: filteredProperties.length,
      units: totalUnits,
      avgVacancy: avgVacancy.toFixed(1),
      avgRentSF: avgRentSF
    };
  }, [filteredProperties]);

  return (
    <div className="app">
      {/* Professional Header */}
      {/* Professional Header */}
      <Navbar>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Portfolio</span>
            <span className="stat-value">{formatNumber(stats.total || 0)}</span>
            <span className="stat-sublabel">Total Properties</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-label">Total Units</span>
            <span className="stat-value">{formatNumber(stats.units || 0)}</span>
            <span className="stat-sublabel">{(parseFloat(stats.avgVacancy) || 0).toFixed(1)}% avg vacancy</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-label">Avg Rent/SF</span>
            <span className="stat-value">
              ${(parseFloat(stats.avgRentSF) || 0).toFixed(2)}
            </span>
            <span className="stat-sublabel">market average</span>
          </div>
        </div>
        <div className="header-actions">
          <div className="view-toggle-group">
            <button
              className={`toggle-option ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => setViewMode('map')}
            >
              Map
            </button>
            <button
              className={`toggle-option ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={`toggle-option ${viewMode === 'split' ? 'active' : ''}`}
              onClick={() => setViewMode('split')}
            >
              Split
            </button>
          </div>

          <button
            className="btn-secondary"
            onClick={() => setShowExportModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 10L8 2M8 10L5 7M8 10L11 7M2 14H14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Export
          </button>

          <input
            ref={fileInputRef}
            type="file"
            id="file-upload"
            accept=".xlsx,.xls,.csv"
            multiple
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <label htmlFor="file-upload" className="btn-primary">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 6L8 14M8 6L5 9M8 6L11 9M2 2H14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Import Data
          </label>
        </div>
      </Navbar>

      {/* Main Application */}
      <div className="app-body">
        {/* Professional Sidebar */}
        <aside className={`sidebar-professional ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-section">
            <h3 className="sidebar-title">Property Filters</h3>

            {/* Filter Tooltip Guide */}
            {showFilterTooltip && (
              <div className="filter-tooltip">
                <div className="tooltip-content">
                  <p>💡 <strong>Tip:</strong> Use the filters below to narrow your search by class, year, unit size, or property type.</p>
                  <button
                    className="tooltip-close"
                    onClick={() => setShowFilterTooltip(false)}
                    aria-label="Close tooltip"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            {/* State Selector Section */}
            <div>
              <h3 className="filter-label">State</h3>
              <div className="filter-group">
                <select
                  className="select-field"
                  onChange={(e) => {
                    const stateCode = e.target.value;
                    if (!stateCode) return;

                    const STATE_COORDINATES = {
                      'FL': { lat: 27.9584, lng: -82.5135, zoom: 6 },
                      'IN': { lat: 39.7781, lng: -86.1541, zoom: 6 },
                      'KS': { lat: 39.0859, lng: -94.5837, zoom: 6 },
                      'KY': { lat: 39.1029, lng: -84.5122, zoom: 6 },
                      'MO': { lat: 39.0634, lng: -94.5767, zoom: 6 },
                      'OH': { lat: 39.9865, lng: -82.9978, zoom: 6 },
                      'TX': { lat: 32.14, lng: -96.89, zoom: 6 }
                    };

                    const target = STATE_COORDINATES[stateCode];
                    if (target && mapRef.current) {
                      mapRef.current.getMap().flyTo({
                        center: [target.lng, target.lat],
                        zoom: target.zoom,
                        essential: true
                      });
                    }
                  }}
                  defaultValue="OH"
                >
                  <option value="" disabled>Select a State...</option>
                  <option value="FL">Florida (FL)</option>
                  <option value="IN">Indiana (IN)</option>
                  <option value="KS">Kansas (KS)</option>
                  <option value="KY">Kentucky (KY)</option>
                  <option value="MO">Missouri (MO)</option>
                  <option value="OH">Ohio (OH)</option>
                  <option value="TX">Texas (TX)</option>
                </select>
              </div>
            </div>

            {/* Building Class */}
            <div className="filter-group">
              <label className="filter-label">
                Building Class
                <Tooltip content="Filter properties by their building quality and age category." position="right" delay={200}>
                  <span style={{
                    marginLeft: '6px',
                    color: '#6b7280',
                    cursor: 'help',
                    fontSize: '14px',
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6'
                  }}>
                    ℹ️
                  </span>
                </Tooltip>
                {filters.buildingClasses.length === 0 && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                    (All)
                  </span>
                )}
              </label>
              <div className="button-group">
                {['A', 'B', 'C'].map(cls => (
                  <button
                    key={cls}
                    className={`button-group-item ${filters.buildingClasses.includes(cls) ? 'active' : ''}`}
                    onClick={() => {
                      const newClasses = filters.buildingClasses.includes(cls)
                        ? filters.buildingClasses.filter(c => c !== cls)
                        : [...filters.buildingClasses, cls];
                      setFilters(prev => ({
                        ...prev,
                        buildingClasses: newClasses
                      }));
                    }}
                  >
                    Class {cls}
                  </button>
                ))}
              </div>
            </div>

            {/* Year Built */}
            <div className="filter-group">
              <label className="filter-label">
                Year Built
                <Tooltip content="Filter properties by the year they were constructed. Enter a minimum and maximum year to show only properties built within that range." position="right" delay={200}>
                  <span style={{
                    marginLeft: '6px',
                    color: '#6b7280',
                    cursor: 'help',
                    fontSize: '14px',
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6'
                  }}>
                    ℹ️
                  </span>
                </Tooltip>
              </label>
              <div className="range-inputs-group">
                <input
                  type="number"
                  className="input-field"
                  placeholder="Min"
                  value={filters.yearRange.min}
                  onChange={(e) => handleYearRangeChange('min', e.target.value)}
                />
                <span className="range-separator">—</span>
                <input
                  type="number"
                  className="input-field"
                  placeholder="Max"
                  value={filters.yearRange.max}
                  onChange={(e) => handleYearRangeChange('max', e.target.value)}
                />
              </div>
            </div>

            {/* Property Type */}
            <div className="filter-group">
              <label className="filter-label">
                Property Type
                <Tooltip content="Filter properties by their architectural style or building type. Select one or more types to narrow your search." position="right" delay={200}>
                  <span style={{
                    marginLeft: '6px',
                    color: '#6b7280',
                    cursor: 'help',
                    fontSize: '14px',
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6'
                  }}>
                    ℹ️
                  </span>
                </Tooltip>
                {filters.propertyTypes.length === 0 && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                    (All)
                  </span>
                )}
              </label>
              <div className="checkbox-list">
                {propertyTypes.length > 0 ? propertyTypes.map(type => (
                  <label key={type} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.propertyTypes.includes(type)}
                      onChange={(e) => {
                        const newTypes = e.target.checked
                          ? [...filters.propertyTypes, type]
                          : filters.propertyTypes.filter(t => t !== type);
                        setFilters(prev => ({
                          ...prev,
                          propertyTypes: newTypes
                        }));
                      }}
                    />
                    <span className="checkbox-label">{type}</span>
                  </label>
                )) : (
                  <p className="no-data">No property types available</p>
                )}
              </div>
            </div>

            {/* Radius Search */}
            <div className="filter-group">
              <label className="filter-label">
                Radius Search
                <Tooltip content="Filter properties within a specific distance from a point on the map. Select a radius, then click on the map to set the center point." position="right" delay={200}>
                  <span style={{
                    marginLeft: '6px',
                    color: '#6b7280',
                    cursor: 'help',
                    fontSize: '14px',
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6'
                  }}>
                    ℹ️
                  </span>
                </Tooltip>
              </label>
              <select
                className="select-field"
                value={filters.radius || ''}
                onChange={(e) => {
                  const newRadius = e.target.value ? parseFloat(e.target.value) : null;
                  setFilters(prev => ({ ...prev, radius: newRadius }));
                  if (!newRadius) {
                    setRadiusCenter(null);
                  }
                }}
              >
                <option value="">No radius filter</option>
                <option value="1">1 mile radius</option>
                <option value="3">3 mile radius</option>
                <option value="5">5 mile radius</option>
                <option value="10">10 mile radius</option>
              </select>
              {filters.radius && (
                <div className="radius-info">
                  {!radiusCenter ? (
                    <p className="filter-hint">Click on map to set center point</p>
                  ) : (
                    <p className="radius-status">
                      Center: {radiusCenter.lat.toFixed(4)}, {radiusCenter.lng.toFixed(4)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Unit Range */}
            <div className="filter-group">
              <label className="filter-label">
                Unit Range
                <Tooltip content="Filter properties by the number of units. Use the slider to set a minimum and maximum unit count range." position="right" delay={200}>
                  <span style={{
                    marginLeft: '6px',
                    color: '#6b7280',
                    cursor: 'help',
                    fontSize: '14px',
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6'
                  }}>
                    ℹ️
                  </span>
                </Tooltip>
              </label>
              <RangeSlider
                min={1}
                max={500}
                value={filters.unitRange}
                onChange={(newRange) => {
                  setFilters(prev => ({
                    ...prev,
                    unitRange: newRange
                  }));
                }}
                step={1}
                label="Units"
              />
            </div>

            {/* Reset button */}
            <button
              className="btn-reset"
              onClick={() => {
                setFilters({
                  buildingClasses: ['B'],
                  propertyTypes: [],
                  yearRange: { min: 1990, max: 2010 },
                  unitRange: { min: 50, max: 500 },
                  radius: null
                });
                setRadiusCenter(null);
                setShowFilterTooltip(true);
              }}
            >
              Reset to Defaults
            </button>
          </div>

          {/* Data Overlays Control - THIS IS WHERE YOUR OVERLAYS SHOULD APPEAR */}
          <DataOverlays
            overlays={overlays}
            setOverlays={setOverlays}
            viewport={viewport}
            dataLayer={tractDataLayer}
            setDataLayer={setTractDataLayer}
            onOverlayDataChange={setOverlayData}
          />

          {/* Opportunity Panel */}
          {/* <OpportunityPanel
            properties={filteredProperties}
            opportunityScores={opportunityScores}
          /> */}
        </aside>

        {/* Main Content Area */}
        <div className="main-content-area">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {/* Map View */}
          {(viewMode === 'map' || viewMode === 'split') && (
            <div className={`map-container ${viewMode === 'split' ? 'split-view' : ''}`}>
              {(loading || tractsLoading || gridLoading) && (
                <div className="minimal-loader">
                  <div className="tiny-spinner"></div>
                  <span>{loading ? 'Updating properties...' : gridLoading ? 'Loading grid...' : 'Loading layers...'}</span>
                </div>
              )}

              {viewport.zoom < 10 && (
                <div className="map-zoom-hint">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 7V13M7 10H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Zoom in to view properties and layers</span>
                </div>
              )}



              <Map
                ref={mapRef}
                initialViewState={viewport}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/light-v11"
                mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN || 'your-mapbox-token-here'}
                onMove={evt => setViewport(evt.viewState)}
                onClick={handleMapClick}
                cursor={filters.radius ? 'crosshair' : 'auto'}
                reuseMaps
              >
                {/* Map Search Bar */}
                <div className="map-search-bar">
                  <div className="map-search-input-wrapper">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                      <path d="M14 14L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={mapSearchTerm}
                      onChange={handleMapSearchChange}
                    />
                    {mapSearching && <span className="map-search-spinner">…</span>}
                  </div>



                  {mapSearchTerm && mapSearchResults.length > 0 && (
                    <div className="map-search-results">
                      {mapSearchResults.map((p) => (
                        <button
                          key={p.id}
                          className="map-search-result-item"
                          onClick={() => handleMapSearchSelect(p)}
                        >
                          <div className="result-title">
                            {p.propertyName || p.propertyAddress || 'Unnamed property'}
                          </div>
                          <div className="result-subtitle">
                            {p.city}, {p.state} · {p.numberOfUnits || '—'} units
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {mapSearchTerm && !mapSearching && mapSearchResults.length === 0 && (
                    <div className="map-search-results map-search-empty">
                      No matches
                    </div>
                  )}
                </div>
                <NavigationControl position="top-right" />
                <ScaleControl position="bottom-right" />

                {/* Grid Cluster Layer */}
                {gridData && viewport.zoom <= 10 && (
                  <Source id="grid-source" type="geojson" data={gridData}>
                    <Layer
                      id="grid-fill"
                      type="fill"
                      paint={{
                        'fill-color': [
                          'interpolate',
                          ['linear'],
                          ['get', 'count'],
                          1, '#fee5d9',
                          10, '#fcae91',
                          50, '#fb6a4a',
                          100, '#de2d26',
                          200, '#a50f15'
                        ],
                        'fill-opacity': 0.7,
                        'fill-outline-color': '#7a1f1f'
                      }}
                      beforeId="waterway-label"
                    />
                    <Layer
                      id="grid-border"
                      type="line"
                      paint={{
                        'line-color': '#ffffff',
                        'line-width': 1,
                        'line-opacity': 0.5
                      }}
                    />
                    <Layer
                      id="grid-count"
                      type="symbol"
                      layout={{
                        'text-field': ['to-string', ['get', 'count']],
                        'text-size': 12,
                        'text-allow-overlap': false
                      }}
                      paint={{
                        'text-color': '#000000',
                        'text-halo-color': '#ffffff',
                        'text-halo-width': 2
                      }}
                    />
                  </Source>
                )}

                {/* Comprehensive Tract Layer with all data sources */}
                {(overlays.censusTracts || overlays.income || overlays.demographics || overlays.eviction) && (
                  <ComprehensiveTractLayer
                    visible={overlays.censusTracts || overlays.income || overlays.demographics || overlays.eviction}
                    dataLayer={overlays.eviction ? 'eviction' : tractDataLayer}
                    viewport={viewport}
                    mapRef={mapRef}
                    onTractClick={(properties, lngLat) => {
                      setSelectedTract(properties);
                    }}
                    onLoadingChange={setTractsLoading}
                  />
                )}

                {/* Crime Data Layer */}
                {overlays.crime && (
                  <CrimeLayer
                    visible={overlays.crime}
                    viewport={viewport}
                    crimeData={overlayData?.crime?.data}
                  />
                )}

                {/* Radius Circle */}
                {radiusCircle && (
                  <Source id="radius" type="geojson" data={radiusCircle}>
                    <Layer
                      id="radius-fill"
                      type="fill"
                      paint={{
                        'fill-color': '#4F46E5',
                        'fill-opacity': 0.1
                      }}
                    />
                    <Layer
                      id="radius-border"
                      type="line"
                      paint={{
                        'line-color': '#4F46E5',
                        'line-width': 2,
                        'line-dasharray': [3, 3]
                      }}
                    />
                  </Source>
                )}

                {/* Radius Center Marker */}
                {radiusCenter && (
                  <Marker
                    latitude={radiusCenter.lat}
                    longitude={radiusCenter.lng}
                  >
                    <div className="radius-center-marker">
                      <div className="pulse-ring"></div>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="3" fill="#4F46E5" />
                        <circle cx="12" cy="12" r="8" stroke="#4F46E5" strokeWidth="2" fill="none" />
                      </svg>
                    </div>
                  </Marker>
                )}

                {/* Property Markers */}
                {filteredProperties.map(property => {
                  const score = opportunityScores.find(s => s.propertyId === property.id);
                  return (
                    property.latitude && property.longitude && (
                      <Marker
                        key={property.id}
                        latitude={property.latitude}
                        longitude={property.longitude}
                        onClick={e => {
                          e.originalEvent.stopPropagation();
                          handlePropertySelect(property);
                        }}
                      >
                        <div
                          className="property-marker"
                          style={{
                            backgroundColor: getMarkerColor(property.buildingClass, score?.score),
                          }}
                        >
                          <span className="marker-label">
                            {property.numberOfUnits || '—'}
                          </span>
                        </div>
                      </Marker>
                    )
                  );
                })}

                {/* Property Popup */}
                {selectedProperty && (
                  <Popup
                    latitude={selectedProperty.latitude}
                    longitude={selectedProperty.longitude}
                    onClose={() => setSelectedProperty(null)}
                    closeOnClick={false}
                    maxWidth="420px"
                  >
                    <div className="popup-professional">
                      <div className="popup-header">
                        <div>
                          <h3>{selectedProperty.propertyName || 'Property Details'}</h3>
                          <p className="popup-address">{selectedProperty.propertyAddress}</p>
                        </div>
                        <span className={`class-indicator class-${(selectedProperty.buildingClass || 'unknown').toLowerCase()}`}>
                          {selectedProperty.buildingClass || 'N/A'}
                        </span>
                      </div>

                      <div className="popup-metrics">
                        <div className="metric">
                          <span className="metric-label">Units</span>
                          <span className="metric-value">{formatNumber(selectedProperty.numberOfUnits)}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Built</span>
                          <span className="metric-value">{selectedProperty.yearBuilt || '—'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Rent/SF</span>
                          <span className="metric-value">
                            ${selectedProperty.rentPerSF && !isNaN(selectedProperty.rentPerSF)
                              ? parseFloat(selectedProperty.rentPerSF).toFixed(2)
                              : '—'}
                          </span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Vacancy</span>
                          <span className="metric-value">
                            {selectedProperty.vacancyPercent && !isNaN(selectedProperty.vacancyPercent)
                              ? parseFloat(selectedProperty.vacancyPercent).toFixed(1)
                              : '0'}%
                          </span>
                        </div>
                      </div>

                      <div className="popup-details">
                        <div className="detail-row">
                          <span>Property Type</span>
                          <span>{selectedProperty.style || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span>Submarket</span>
                          <span>{selectedProperty.submarket || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span>Owner</span>
                          <span>{selectedProperty.ownerName || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </Popup>
                )}
              </Map>

              {/* Radius Info Overlay */}
              {filters.radius && !radiusCenter && (
                <div className="map-overlay">
                  <div className="overlay-message">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#4F46E5" />
                      <circle cx="12" cy="9" r="2.5" fill="white" />
                    </svg>
                    <p>Click anywhere on the map to set the center for your {filters.radius} mile radius search</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {(viewMode === 'list' || viewMode === 'split') && (
            <div className={`list-container ${viewMode === 'split' ? 'split-view' : ''}`}>
              <PropertyList
                properties={listProperties}
                opportunityScores={opportunityScores}
                onPropertySelect={handlePropertySelect}
                selectedProperty={selectedProperty}
                pagination={pagination}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                onSortChange={handleListSortChange}
                onSearch={handleListSearch}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tract Info Panel */}
      {(selectedTract || (selectedProperty && (selectedProperty.GEOID || selectedProperty.geoid))) && (
        <TractInfoPanel
          tract={selectedTract || selectedProperty}
          onClose={() => {
            setSelectedTract(null);
            // Optionally clear property selection if that's what triggered it
            if (!selectedTract && selectedProperty) {
              setSelectedProperty(null);
            }
          }}
          overlays={overlays}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          properties={filteredProperties} // Fallback/initial data
          onFetchAll={async () => {
            if (!mapRef.current) return [];
            const map = mapRef.current.getMap();
            const bounds = map.getBounds();
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();

            const filterParams = {
              buildingClasses: filters.buildingClasses,
              propertyTypes: filters.propertyTypes,
              yearMin: filters.yearRange?.min,
              yearMax: filters.yearRange?.max,
              unitsMin: filters.unitRange?.min,
              unitsMax: filters.unitRange?.max,
              radius: filters.radius,
              lat: radiusCenter?.lat,
              lng: radiusCenter?.lng
            };

            const res = await propertyAPI.getPropertiesInBounds({
              swLat: sw.lat,
              swLng: sw.lng,
              neLat: ne.lat,
              neLng: ne.lng,
              ...filterParams
            });
            return res.data;
          }}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Upload Progress */}
      {uploadProgress !== null && (
        <div className="upload-progress-bar">
          <div className="progress-content">
            <span>Uploading data...</span>
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span>{uploadProgress}%</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;                         
